import { CONNECTION_STORAGE_KEYS } from '../stores/connectionStore';
import { FACILITY_STORAGE_KEY } from '../stores/globalStore';

/**
 * Where the user was when their sign-in ended, so signing in again returns
 * them there instead of to their start page.
 *
 * Kept in sessionStorage, which survives the Keycloak round trip in the same
 * tab. Read once by StartFormRedirect (App.tsx) when the sign-in has resolved
 * a facility. It is only a place to go, never a permission: the page's own
 * route guard still checks the role, and when a different person signs in on
 * the tab, validateConnectionOwner drops the connection, StartFormRedirect
 * sends them to the connection picker and this is discarded.
 */
export const RETURN_PATH_KEY = 'dietech_return_after_sign_in';

/** Pages that are not a place to come back to. */
const NOT_A_PLACE = ['/', '/connection'];

/** A same-site path ("/people/12?tab=diet"), never another origin ("//evil", "https://..."). */
export function isSafeReturnPath(path: string | null | undefined): path is string {
  if (!path || !path.startsWith('/') || path.startsWith('//') || path.includes('\\')) return false;
  const pathname = path.split(/[?#]/)[0];
  return !NOT_A_PLACE.includes(pathname);
}

export function saveReturnPath(path: string): void {
  if (!isSafeReturnPath(path)) return;
  try {
    sessionStorage.setItem(RETURN_PATH_KEY, path);
  } catch {
    // Storage unavailable: the user starts on their start page, as before.
  }
}

/** The saved path, if any and still safe. Does not remove it (see clearReturnPath). */
export function peekReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem(RETURN_PATH_KEY);
    return isSafeReturnPath(path) ? path : null;
  } catch {
    return null;
  }
}

export function clearReturnPath(): void {
  try {
    sessionStorage.removeItem(RETURN_PATH_KEY);
  } catch {
    // Storage unavailable: nothing was saved there either.
  }
}

/**
 * Where StartFormRedirect sends a resolved sign-in: back to the saved page when
 * signing in finished normally, else where it would have gone anyway. The
 * connection picker (a different person signed in on the tab, or no connection)
 * and a stopped sign-in (no facility access, a load error) never use it.
 */
export function startTargetWithReturn(startRoute: string, returnTo: string | null, signInStopped: boolean): string {
  if (!isSafeReturnPath(returnTo) || signInStopped || startRoute === '/connection') return startRoute;
  return returnTo;
}

/**
 * Log In Again after the session expired: forget the dead sign-in and every
 * other per-tab setting as before, but keep the tab's connection and facility
 * and remember the page, so signing in again picks up where the user was.
 * oidc-client-ts keeps its user in sessionStorage too, so clearing it signs
 * the tab out (a stale user whose access token had not run out yet would
 * otherwise read as signed in and 401 straight back into the dialog).
 */
export function prepareSignInAgain(currentPath: string): void {
  const keep = [...CONNECTION_STORAGE_KEYS, FACILITY_STORAGE_KEY];
  try {
    const kept = keep
      .map((key) => [key, sessionStorage.getItem(key)] as const)
      .filter((entry): entry is readonly [typeof keep[number], string] => entry[1] !== null);
    sessionStorage.clear();
    for (const [key, value] of kept) sessionStorage.setItem(key, value);
  } catch {
    // Storage unavailable: nothing to keep or clear.
  }
  saveReturnPath(currentPath);
}
